import { useState } from 'react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { DataTable } from '../../../components/ui/data-table'
import { 
  Search, 
  UserPlus, 
  MoreHorizontal, 
  User2, 
  Printer, 
  FileEdit, 
  CalendarPlus,
  Archive,
  RotateCcw,
  AlertCircle
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs'
import { useArchivePatient } from '../hooks/useArchivePatient'
import { useUnarchivePatient } from '../hooks/useUnarchivePatient'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { columns } from './columns'
import { usePatients } from '../hooks/usePatients'
import { NewPatientDialog } from './NewPatientDialog'
import { PatientDetailsDialog } from './PatientDetailsDialog'
import { PrintPatientRecordDialog } from './PrintPatientRecordDialog'
import { EditPatientDialog } from './EditPatientDialog'
import { NewVisitDialog } from '../../visits/components/NewVisitDialog'
import { Patient } from '../../../lib/types/patient'
import { Visit } from '../../visits/types/visit'
import { useCreateVisit } from '../../visits/hooks/useCreateVisit'

export function PatientList() {
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [archivedSearchTerm, setArchivedSearchTerm] = useState('')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [patientToModify, setPatientToModify] = useState<Patient | null>(null)
  
  const { data: patients, isLoading } = usePatients({
    status: activeTab === 'active' ? 'active' : 'inactive'
  })
  const { mutate: createVisit } = useCreateVisit()
  const [isNewPatientDialogOpen, setIsNewPatientDialogOpen] = useState(false)
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null)
  const [dialogType, setDialogType] = useState<'details' | 'print' | 'edit' | 'visit' | null>(null)
  // Filter patients based on search term for current tab
  const filteredPatients = patients?.filter((patient) => {
    const searchTerm = activeTab === 'active' ? activeSearchTerm : archivedSearchTerm
    const searchLower = searchTerm.toLowerCase()
    return (
      patient.firstName.toLowerCase().includes(searchLower) ||
      patient.lastName.toLowerCase().includes(searchLower) ||
      `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
      patient.phone.includes(searchTerm) ||
      patient.email?.toLowerCase().includes(searchLower) ||
      patient.id?.toLowerCase().includes(searchLower)
    )
  }) || []

  const selectedPatient = patients?.find(p => p.id === selectedPatientId)

  const { mutate: archivePatient } = useArchivePatient()
  const { mutate: unarchivePatient } = useUnarchivePatient()

  const handleViewDetails = (patient: Patient) => {
    if (!patient.id) return
    setSelectedPatientId(patient.id)
    setDialogType('details')
  }

  const handlePrintRecord = (patient: Patient) => {
    if (!patient.id) return
    setSelectedPatientId(patient.id)
    setDialogType('print')
  }

  const handleEditPatient = (patient: Patient) => {
    if (!patient.id) return
    setSelectedPatientId(patient.id)
    setDialogType('edit')
  }

  const handleNewVisit = (patient: Patient) => {
    if (!patient.id) return
    setSelectedPatientId(patient.id)
    setDialogType('visit')
  }

  const handleArchivePatient = (patient: Patient) => {
    setPatientToModify(patient)
    setConfirmDialogOpen(true)
  }

  const handleUnarchivePatient = (patient: Patient) => {
    setPatientToModify(patient)
    setConfirmDialogOpen(true)
  }

  const handleConfirmAction = () => {
    if (!patientToModify?.id) return

    if (patientToModify.status === 'active') {
      archivePatient(patientToModify.id)
    } else {
      unarchivePatient(patientToModify.id)
    }
    
    setConfirmDialogOpen(false)
    setPatientToModify(null)
  }

  const ActionsCell = ({ patient }: { patient: Patient }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={(e) => {
            e.stopPropagation()
            handleViewDetails(patient)
          }}
        >
          <User2 className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={(e) => {
            e.stopPropagation()
            handlePrintRecord(patient)
          }}
        >
          <Printer className="mr-2 h-4 w-4" />
          Print Record
        </DropdownMenuItem>
        {patient.status === 'active' && (
          <>
            <DropdownMenuItem 
              onClick={(e) => {
                e.stopPropagation()
                handleEditPatient(patient)
              }}
            >
              <FileEdit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => {
                e.stopPropagation()
                handleNewVisit(patient)
              }}
            >
              <CalendarPlus className="mr-2 h-4 w-4" />
              New Visit
            </DropdownMenuItem>
          </>
        )}
        {patient.status === 'active' ? (
          <DropdownMenuItem 
            onClick={(e) => {
              e.stopPropagation()
              handleArchivePatient(patient)
            }}
          >
            <Archive className="mr-2 h-4 w-4" />
            Archive
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem 
            onClick={(e) => {
              e.stopPropagation()
              handleUnarchivePatient(patient)
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Unarchive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const handleCreateVisit = (data: Partial<Visit>) => {
    if (!data.patientId || !data.clinicId || !data.doctorId || !data.type || !data.startTime || !data.duration || !data.reason) {
      console.error('Missing required fields for visit creation');
      return;
    }

    const visitInput = {
      patientId: data.patientId,
      clinicId: data.clinicId,
      doctorId: data.doctorId,
      type: data.type,
      startTime: data.startTime,
      duration: data.duration,
      reason: data.reason,
      initialNotes: data.initialNotes,
      originalVisitId: data.originalVisitId,
      isUrgent: data.isUrgent
    };

    createVisit(visitInput)
  }

  const closeDialog = () => {
    setSelectedPatientId(null)
    setDialogType(null)
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-background to-background rounded-lg -z-10" />
        <Tabs defaultValue="active" value={activeTab} onValueChange={(value) => setActiveTab(value as 'active' | 'archived')}>
          <div className="relative space-y-4 p-6 rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold text-foreground">Patients</h1>
                <p className="text-sm text-muted-foreground">
                  Manage and view patient records
                </p>
              </div>
              <Button 
                onClick={() => setIsNewPatientDialogOpen(true)}
                className="sm:self-start flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
              >
                <UserPlus className="h-4 w-4" />
                New Patient
              </Button>
            </div>

            <TabsList className="grid w-full max-w-[400px] grid-cols-2">
              <TabsTrigger value="active">Active Patients</TabsTrigger>
              <TabsTrigger value="archived">Archived Patients</TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {/* Active Patients Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search active patients by name, phone, email, or ID" 
                  value={activeSearchTerm}
                  onChange={(e) => setActiveSearchTerm(e.target.value)}
                  className="pl-9 bg-background/50 backdrop-blur-sm"
                />
              </div>
            </TabsContent>

            <TabsContent value="archived">
              {/* Archived Patients Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search archived patients by name, phone, email, or ID" 
                  value={archivedSearchTerm}
                  onChange={(e) => setArchivedSearchTerm(e.target.value)}
                  className="pl-9 bg-background/50 backdrop-blur-sm"
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Table Section */}
      <div className="rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm shadow-md">
        <DataTable 
          columns={[
            ...columns,
            {
              id: 'actions',
              header: 'Actions',
              cell: ({ row }) => <ActionsCell patient={row.original} />,
            },
          ]} 
          data={filteredPatients} 
          isLoading={isLoading}
        />
      </div>

      {/* Dialogs */}
      <NewPatientDialog 
        open={isNewPatientDialogOpen} 
        onOpenChange={setIsNewPatientDialogOpen} 
      />

      {selectedPatientId && dialogType === 'details' && (
        <PatientDetailsDialog 
          open={true} 
          onOpenChange={(open) => !open && closeDialog()} 
          patientId={selectedPatientId} 
        />
      )}

      {selectedPatientId && dialogType === 'print' && (
        <PrintPatientRecordDialog 
          open={true} 
          onOpenChange={(open) => !open && closeDialog()} 
          patientId={selectedPatientId} 
        />
      )}

      {selectedPatientId && dialogType === 'edit' && (
        <EditPatientDialog 
          open={true} 
          onOpenChange={(open) => !open && closeDialog()} 
          patientId={selectedPatientId} 
        />
      )}

      {selectedPatient?.id && dialogType === 'visit' && (
        <NewVisitDialog 
          open={true} 
          onOpenChange={(open) => !open && closeDialog()} 
          patient={{
            id: selectedPatient.id,
            firstName: selectedPatient.firstName,
            lastName: selectedPatient.lastName
          }}
          clinicId="C1001" // TODO: Get from auth context
          doctorId="D1001" // TODO: Get from auth context
          onSubmit={handleCreateVisit}
        />
      )}

      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-foreground">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {patientToModify?.status === 'active' ? 'Archive' : 'Unarchive'} Patient Record
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {patientToModify?.status === 'active' ? (
                <>
                  Are you sure you want to archive {patientToModify.firstName} {patientToModify.lastName}'s record? 
                  This action will remove the patient from active records but their information will be preserved.
                </>
              ) : (
                <>
                  Are you sure you want to unarchive {patientToModify?.firstName} {patientToModify?.lastName}'s record? 
                  This action will restore the patient to active records.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPatientToModify(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={patientToModify?.status === 'active' ? 
                "bg-destructive hover:bg-destructive/90 text-destructive-foreground" :
                "bg-primary hover:bg-primary/90"
              }
            >
              {patientToModify?.status === 'active' ? 'Archive' : 'Unarchive'} Patient
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
